import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * Reserve button — calls /api/checkout/session on mount to detect whether
 * apparel drop is live. If the backend responds with {status:'pending_skus'}
 * (either empty items or Stripe not configured), the button remains disabled
 * with tooltip 'Available at drop'.
 */
export const ReserveButton = ({ items = [], label = "Reserve" }) => {
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(true);
  const [ready, setReady] = useState(false); // when true, a session URL was returned

  // Silent probe on mount to determine live vs pending
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.post("/checkout/session", {
          items,
          origin_url: window.location.origin,
        });
        if (!alive) return;
        if (res.data?.status === "pending_skus") {
          setPending(true);
        } else if (res.data?.url) {
          setPending(false);
          setReady(true);
        }
      } catch (_e) {
        // network or auth error — keep pending state
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const click = async () => {
    if (busy || pending) return;
    if (!ready) return;
    setBusy(true);
    try {
      const res = await api.post("/checkout/session", {
        items,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.message("Available at drop.");
        setPending(true);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={click}
      disabled={busy || pending}
      title={pending ? "Available at drop" : ""}
      data-testid="reserve-button"
      data-pending={pending ? "true" : "false"}
      aria-disabled={pending || busy ? "true" : "false"}
      className={`inline-flex items-center gap-3 px-5 py-3 rounded-full text-[11px] tracking-[0.3em] uppercase transition-colors duration-500 ${
        pending
          ? "border border-white/25 text-white/60 cursor-not-allowed opacity-80"
          : "bg-silver-100 text-black hover:bg-white"
      }`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${
          pending ? "bg-white/50" : "bg-black"
        } animate-pulse`}
      />
      {busy ? "Checking…" : pending ? `${label} · Coming Soon` : label}
    </button>
  );
};

export default ReserveButton;
